import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import $api from "http/index";
import { listEmployees } from "service/HRService";
import Notification from "utils/Notifications";
import { PageHeader, DataTable, Card, UnvButton, UnvModal, Icon } from "components";

import s from "./GroupsPage.module.scss";

export default function GroupsPage() {
  const { t } = useTranslation('common');
  const [items, setItems] = useState([]);
  const [directions, setDirections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [advancing, setAdvancing] = useState(null);
  const [notify, setNotify] = useState({ isOpen: false });

  const FORMS = [
    { value: "full_time", label: t('groups.form_full_time') },
    { value: "part_time", label: t('groups.form_part_time') },
    { value: "evening", label: t('groups.form_evening_full') },
    { value: "distance", label: t('groups.form_distance_full') },
  ];
  const LANGS = [
    { value: "ru", label: t('groups.lang_ru_short') },
    { value: "ky", label: t('groups.lang_ky_short') },
    { value: "en", label: t('groups.lang_en_short') },
  ];

  const reload = () => $api.get("groups/").then((r) => setItems(r.data?.results || r.data || []));
  useEffect(() => {
    reload();
    $api.get("directions/").then((r) => setDirections(r.data?.results || r.data || [])).catch(() => {});
    listEmployees({ status: "active", page_size: 200 })
      .then((r) => setTeachers(r.data?.results || r.data || []))
      .catch(() => {});
  }, []);

  const save = async () => {
    try {
      if (editing.id) await $api.patch(`groups/${editing.id}/`, editing);
      else await $api.post("groups/", editing);
      setNotify({ isOpen: true, message: t('groups.saved'), type: "success" });
      setEditing(null);
      reload();
    } catch (e) { setNotify({ isOpen: true, message: e.response?.data?.detail || t('groups.error'), type: "error" }); }
  };

  const advance = async () => {
    try {
      const body = { group_id: advancing.id };
      if (advancing.target_group_id) body.target_group_id = advancing.target_group_id;
      if (advancing.target_course) body.target_course = advancing.target_course;
      const r = await $api.post("students/students/bulk-advance-group/", body);
      setNotify({ isOpen: true, message: t('groups.transfer_summary', { moved: r.data.moved_count, skipped: r.data.skipped_count }), type: "success" });
      setAdvancing(null);
      reload();
    } catch (e) { setNotify({ isOpen: true, message: e.response?.data?.detail || t('groups.error'), type: "error" }); }
  };

  return (
    <div className={s.page}>
      <PageHeader
        icon={<Icon name="group" size={28} />}
        title={t('groups.page_title')}
        subtitle={t('auto.groupspage.subtitle', 'Учебные группы и кураторы')}
        actions={
          <UnvButton variant="primary" iconLeft="plus"
            onClick={() => setEditing({ form_education: "full_time", language: "ru", is_active: true, count_students: 0 })}>
            {t('groups.create_group_btn')}
          </UnvButton>
        }
      />
      <Card padding="none">
        <Card.Body>
          <DataTable
            data={items}
            rowKey="id"
            columns={[
              { key: 'title', label: t('groups.col_name'), sortable: true, render: (v) => <b>{v}</b> },
              { key: 'direction', label: t('groups.col_direction'), render: (_, g) => g.direction?.direction_name || g.direction || '—' },
              { key: 'course', label: t('groups.col_course'), sortable: true },
              { key: 'year_admission', label: t('groups.col_year_admission'), sortable: true },
              { key: 'form_education', label: t('groups.col_form'), render: (v) => FORMS.find((f) => f.value === v)?.label },
              { key: 'language', label: t('groups.col_lang'), render: (v) => LANGS.find((l) => l.value === v)?.label },
              { key: 'curator', label: t('groups.col_curator'), render: (_, g) => g.curator?.full_name || (g.curator ? `#${g.curator}` : '—') },
              { key: 'count_students', label: t('groups.col_students_short'), align: 'right', sortable: true },
              {
                key: 'actions',
                label: '',
                render: (_, g) => (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <UnvButton variant="ghost" size="sm" iconLeft="edit" onClick={() => setEditing(g)}>{t('groups.edit_short')}</UnvButton>
                    <UnvButton variant="secondary" size="sm" iconLeft="arrow-right" onClick={() => setAdvancing(g)}>{t('groups.next_course_btn')}</UnvButton>
                  </div>
                ),
              },
            ]}
          />
        </Card.Body>
      </Card>

      <UnvModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? t('groups.edit_group_title') : t('groups.new_group_title')}
        size="md"
      >
        {editing && (
          <>
            <div className={s.field}><label>{t('groups.col_name')}</label>
              <input className="unv-input" value={editing.title || ""}
                     onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
            <div className={s.field}><label>{t('groups.col_direction')}</label>
              <select className="unv-input" value={editing.direction?.id || editing.direction || ""}
                      onChange={(e) => setEditing({ ...editing, direction: Number(e.target.value) })}>
                <option value="">—</option>
                {directions.map((d) => (<option key={d.id} value={d.id}>{d.direction_name}</option>))}
              </select></div>
            <div className={s.field}><label>{t('groups.col_year_admission')}</label>
              <input type="number" className="unv-input" value={editing.year_admission || ""}
                     onChange={(e) => setEditing({ ...editing, year_admission: Number(e.target.value) })} /></div>
            <div className={s.field}><label>{t('groups.form_education_label')}</label>
              <select className="unv-input" value={editing.form_education}
                      onChange={(e) => setEditing({ ...editing, form_education: e.target.value })}>
                {FORMS.map((f) => (<option key={f.value} value={f.value}>{f.label}</option>))}
              </select></div>
            <div className={s.field}><label>{t('groups.language_label')}</label>
              <select className="unv-input" value={editing.language}
                      onChange={(e) => setEditing({ ...editing, language: e.target.value })}>
                {LANGS.map((l) => (<option key={l.value} value={l.value}>{l.label}</option>))}
              </select></div>
            <div className={s.field}><label>{t('groups.col_curator')}</label>
              <select className="unv-input" value={editing.curator?.id || editing.curator || ""}
                      onChange={(e) => setEditing({ ...editing, curator: Number(e.target.value) || null })}>
                <option value="">—</option>
                {teachers.map((t2) => (<option key={t2.id} value={t2.id}>{t2.full_name || `${t2.surname} ${t2.first_name}`}</option>))}
              </select></div>
            <div className={s.field}><label>{t('groups.students_count_label')}</label>
              <input type="number" className="unv-input" value={editing.count_students || 0}
                     onChange={(e) => setEditing({ ...editing, count_students: Number(e.target.value) })} /></div>
            <div className={s.field}><label>
              <input type="checkbox" checked={!!editing.is_active}
                     onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> {t('groups.active_label')}
            </label></div>
          </>
        )}
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setEditing(null)}>{t('groups.cancel')}</UnvButton>
          <UnvButton variant="primary" iconLeft="save" onClick={save}>{t('groups.save')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>

      <UnvModal
        open={!!advancing}
        onClose={() => setAdvancing(null)}
        title={advancing ? t('groups.advance_title', { title: advancing.title }) : ''}
        size="sm"
      >
        {advancing && (
          <>
            <p>{t('groups.advance_desc')}</p>
            <div className={s.field}><label>{t('groups.target_group_opt')}</label>
              <select className="unv-input" value={advancing.target_group_id || ""}
                      onChange={(e) => setAdvancing({ ...advancing, target_group_id: Number(e.target.value) || null })}>
                <option value="">{t('groups.keep_same')}</option>
                {items.filter((x) => x.id !== advancing.id).map((g) => (
                  <option key={g.id} value={g.id}>{g.title}</option>
                ))}
              </select></div>
            <div className={s.field}><label>{t('groups.target_course_opt')}</label>
              <input type="number" className="unv-input" value={advancing.target_course || ""}
                     onChange={(e) => setAdvancing({ ...advancing, target_course: Number(e.target.value) || null })} /></div>
          </>
        )}
        <UnvModal.Footer>
          <UnvButton variant="ghost" onClick={() => setAdvancing(null)}>{t('groups.cancel')}</UnvButton>
          <UnvButton variant="primary" iconLeft="arrow-right" onClick={advance}>{t('groups.transfer_btn')}</UnvButton>
        </UnvModal.Footer>
      </UnvModal>
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}
